<template>
  <WizardModal
    modal-id="wizard-modal"
    :is-active="true"
    :is-add="isAdd"
    :wizard-title="wizardTitle"
    :steps="wizardSteps"
    :step-icons="wizardStepIcons"
    :finishAtStep="finishAtStep"
    :current-step="currentStep"
    :show-previous-button="true"
    :show-save-button="false"
    :show-next-button="showNextButton"
    :show-finish-button="showFinishButton"
    :activate-btn-save="true"
    :loading="isLoading"
    :finish-loading="isLoading"
    @close-wizard="closeWizard"
    @prev-step="prevStep"
    @next-step="nextStep"
    @on-submit="nextStep"
    >
    <template #step-0="{ currentStep, loading }" v-if="wizardKey !== 0 || isAdd">
      <discover-cameras
        ref="refDiscoverCameras"
        :cameras="cameras"
        :dvr-options="dvrOptions"
        :is-busy="isBusy"
        :item-data-extra="itemDataExtra"
        @select-dvrs="getCameras"
      />
    </template>
    <template #step-1="{ currentStep, loading }">
      <extra-info
        ref="refExtraInfo"
        :center-options="centerOptions"
        :item-data-extra="itemDataExtra"
        :reseller-options="resellerOptions"
        :sector-options="sectorOptions"
        :unit-options="unitOptions"
      />
    </template>
  </WizardModal>
</template>

<script>
import { computed, ref, onMounted } from "@vue/composition-api";
import { useToast } from "vue-toastification/composition";
import DiscoverCameras from "./DiscoverCameras.vue";
import ExtraInfo from "./ExtraInfo.vue";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import WizardModal from "@/components/WizardModal/Index.vue";
import store from "@/store";

export default {
  components: {
    DiscoverCameras,
    ExtraInfo,
    WizardModal
  },
  props: {
    centerOptions: {
      type: Array,
      default: []
    },
    dvrOptions: {
      type: Array,
      default: []
    },
    resellerOptions: {
      type: Array,
      default: []
    },
    sectorOptions: {
      type: Array,
      default: []
    },
    unitOptions: {
      type: Array,
      default: []
    },
    isAdd: {
      type: Boolean,
      default: true
    },
    preselectedDvr: {
      type: [Number, String],
      default: null
    }
  },
  setup (props, { emit }) {
    //COMPOSABLES
    const toast = useToast();

    //DATA
    const cameras = ref([]);

    const currentStep = ref(0);

    const refExtraInfo = ref(null);

    const finishAtStep = ref(1);

    const itemDataExtra = ref({
      dvrs: [],
      cameras: [],
      id_center: null,
      id_sector: null,
      id_reseller: null,
      id_unit: null,
      id_dvr: null,
      delayed_record_minutes: "00:05:00",
      forward_record_minutes: "00:05:00",
      is_fisheye: false,
      send_to_seproban: false
    });

    const isBusy = ref(false);

    const isLoading = ref(false);

    const isWizardActive = ref(false);

    const refDiscoverCameras = ref(null);

    const wizardKey = ref(0);

    const wizardTitle = ref('Actualizar cámaras');

    const wizardSteps = ref(["Seleccionar cámaras", "Información adicional"]);

    const wizardStepIcons = ref(["VideoIcon", "InfoIcon"]);

    //COMPUTED
    const showFinishButton = computed(() => (props.isAdd && currentStep.value === 1) || (props.isAdd && (currentStep.value === finishAtStep.value)));

    const showNextButton = computed(() => (currentStep.value < finishAtStep.value));

    //METHODS
    const closeWizard = () => {
      currentStep.value = 0;
      resetData();

      emit('close-wizard');
    }
    const getCurrentDvr = (dvrId) => {
      const currentDvrSelected = props.dvrOptions.find(i => i.value === dvrId);
      itemDataExtra.value.id_unit = currentDvrSelected?.id_unit
    }

    const getCameras = async (dvr) => {
      getCurrentDvr(dvr)
      if(!dvr) return;
      isBusy.value = true;

      await store.dispatch("hanwha_camera/discover", { dvrs: [dvr]})
        .then((response) => {
          const  { data } = response;

          cameras.value = data;
          itemDataExtra.value.cameras = data.filter(
            camera => camera.selected
          );
        })
        .catch((response) => {
          toast({
            component: ToastificationContent,
            props: {
              title: response.response.data.message,
              icon: "AlertTriangleIcon",
              variant: "danger",
            },
          });
        })
        .finally(() =>  isBusy.value = false);
    }

    const nextStep = async () => {
      if (isLoading.value) return;
      isLoading.value = true;
      let isValid = true;

      try {
        if (currentStep.value === 0 && refDiscoverCameras.value?.validateStep) {
          refDiscoverCameras.value.showCamerasError = false;

          const discoverCamerasValidated   = await refDiscoverCameras.value.validateStep();
          const discoverCamerasWithCameras = itemDataExtra.value.cameras.length;

          if (!discoverCamerasWithCameras) {
            refDiscoverCameras.value.showCamerasError = true;
          }

          isValid = (discoverCamerasValidated && discoverCamerasWithCameras);
        } else if (currentStep.value === 1 && refExtraInfo.value?.validateStep) {
          isValid = await refExtraInfo.value.validateStep();
        }

        if (isValid) {
          if (currentStep.value === finishAtStep.value) {
            await submit();
            return
          }

          currentStep.value++;
        }
      } catch (error) {
        console.error("Error during step validation:", error);
      } finally {
        isLoading.value = false;
      }
    }

    const prevStep = () => {
      if (isLoading.value) return;
      if (currentStep.value > 0) {
        currentStep.value--;
      }
    }

    const resetData = () => {
      itemDataExtra.value = {
        dvrs: [],
        cameras: [],
        id_center: null,
        id_sector: null,
        id_reseller: null,
        id_unit: null,
        id_dvr: null,
        delayed_record_minutes: "00:05:00",
        forward_record_minutes: "00:05:00",
        is_fisheye: false,
        send_to_seproban: false
      }
    }

    const submit = async () => {
      await store.dispatch('hanwha_camera/add', itemDataExtra.value)
        .then(response => {
          const { message } = response;

          toast({
            component: ToastificationContent,
            props: {
              title: message,
              icon: "CheckIcon",
              variant: "success",
            },
          });

          resetData();

          emit('close-wizard');
          emit('refresh');
        })
        .catch((response) => {
          toast({
            component: ToastificationContent,
            props: {
              title: response.response.data.message,
              icon: "AlertTriangleIcon",
              variant: "danger",
            },
          });
        });
    }

    onMounted(() => {
      if (props.preselectedDvr) {
        itemDataExtra.value.id_dvr = props.preselectedDvr;
        getCameras(props.preselectedDvr);
      }
    });

    return {
      cameras,
      currentStep,
      finishAtStep,
      isBusy,
      isLoading,
      isWizardActive,
      itemDataExtra,
      refDiscoverCameras,
      refExtraInfo,
      wizardKey,
      wizardTitle,
      wizardTitle,
      wizardSteps,
      wizardStepIcons,

      showFinishButton,
      showNextButton,

      closeWizard,
      getCameras,
      prevStep,
      nextStep,
      submit
    }
  }
}
</script>
